import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllHosts, updateEntryInstructionEmails } from "../store/thunks/HostActions";

function HostEntryInstructionsAll() {
  const dispatch = useDispatch();
  const { loading, hostList, error } = useSelector((state) => state.allHosts);
  const [savingId, setSavingId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [rowsState, setRowsState] = useState({});

  useEffect(() => {
    dispatch(getAllHosts({ limit: 500, status: "all" }));
  }, [dispatch]);

  const rows = useMemo(() => (Array.isArray(hostList) ? hostList : []), [hostList]);

  // initialize editable state when list loads
  useEffect(() => {
    // Important: don't wipe in-progress edits (like newEmail) whenever the list refreshes.
    // Only initialize missing rows.
    setRowsState((prev) => {
      const next = { ...prev };
      rows.forEach((h) => {
        if (next[h._id]) return;
        next[h._id] = {
          receive: h.receiveEntryInstructionsEmails ?? false,
          recipients: Array.isArray(h.entryInstructionsAdditionalEmails) ? h.entryInstructionsAdditionalEmails : [],
          newEmail: "",
        };
      });
      return next;
    });
  }, [rows]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const getEntryForHost = (host) => {
    const existing = rowsState[host._id];
    if (existing) return existing;
    return {
      receive: host.receiveEntryInstructionsEmails ?? false,
      recipients: Array.isArray(host.entryInstructionsAdditionalEmails) ? host.entryInstructionsAdditionalEmails : [],
      newEmail: "",
    };
  };

  const persistEntry = async (hostId, entry, { refetch = false } = {}) => {
    setSavingId(hostId);
    setSaveError(null);
    const result = await dispatch(
      updateEntryInstructionEmails({
        hid: hostId,
        receiveEntryInstructionsEmails: entry.receive,
        entryInstructionsAdditionalEmails: entry.recipients,
      })
    );
    setSavingId(null);

    if (updateEntryInstructionEmails.rejected.match(result)) {
      const errMsg = result.payload?.error || result.error?.message || "Failed to save";
      setSaveError({ hostId, message: errMsg });
      return false;
    }

    // Avoid refetching the whole list on every click; it feels like a page refresh.
    // Only refetch when explicitly requested (e.g. after manual "Save").
    if (refetch) dispatch(getAllHosts({ limit: 500, status: "all" }));
    return true;
  };

  const handleToggle = async (host, checked) => {
    const prevEntry = getEntryForHost(host);
    const nextEntry = { ...prevEntry, receive: checked };
    setRowsState((prev) => ({ ...prev, [host._id]: nextEntry }));
    await persistEntry(host._id, nextEntry);
  };

  const handleAddRecipient = async (host) => {
    const entry = getEntryForHost(host);
    if (entry.recipients.length >= 5) return;
    const email = (entry.newEmail || "").trim().toLowerCase();
    if (!email || !validateEmail(email)) return;
    if (entry.recipients.includes(email)) return;
    const nextEntry = { ...entry, recipients: [...entry.recipients, email], newEmail: "" };
    setRowsState((prev) => ({ ...prev, [host._id]: nextEntry }));
    await persistEntry(host._id, nextEntry);
  };

  const handleRemoveRecipient = async (host, email) => {
    const entry = getEntryForHost(host);
    const nextEntry = { ...entry, recipients: entry.recipients.filter((e) => e !== email) };
    setRowsState((prev) => ({ ...prev, [host._id]: nextEntry }));
    await persistEntry(host._id, nextEntry);
  };

  const handleSave = async (host) => {
    const entry = getEntryForHost(host);
    if (entry.recipients.length > 5) return;
    await persistEntry(host._id, entry, { refetch: true });
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Entry Instructions Recipients</h1>
          <p className="text-sm text-zinc-600">
            All property managers with entry-instructions email settings.
          </p>
        </div>
      </div>

      {loading && (
        <div className="p-4 bg-white border border-zinc-200 rounded-lg shadow-sm text-sm text-zinc-600">
          Loading hosts...
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Failed to load hosts: {error}
        </div>
      )}

      {saveError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Save failed: {saveError.message}
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-auto rounded-lg border border-zinc-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-zinc-200">
            <thead className="bg-zinc-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Internal Notes</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Opt-In</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Recipients (only)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {rows.map((host) => (
                <tr key={host._id}>
                  <td className="px-4 py-3 text-sm text-zinc-900">
                    {host.firstName} {host.lastName}
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-700">{host.company || "—"}</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 whitespace-pre-wrap break-words min-w-[20rem]">
                    {host.internalHostNotes || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={rowsState[host._id]?.receive ?? false}
                      onChange={(e) => handleToggle(host, e.target.checked)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-700">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(rowsState[host._id]?.recipients || []).map((email) => (
                        <span key={email} className="px-2 py-1 rounded-full bg-zinc-100 text-xs text-zinc-800 flex items-center gap-2">
                          {email}
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveRecipient(host, email)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                        placeholder="Add recipient email"
                        value={rowsState[host._id]?.newEmail || ""}
                        onChange={(e) =>
                          setRowsState((prev) => ({
                            ...prev,
                            [host._id]: { ...(prev[host._id] || {}), newEmail: e.target.value },
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddRecipient(host);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="px-3 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700"
                        onClick={() => handleAddRecipient(host)}
                      >
                        Add
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      type="button"
                      onClick={() => handleSave(host)}
                      disabled={savingId === host._id}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                    >
                      {savingId === host._id ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-zinc-600">
                    No hosts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default HostEntryInstructionsAll;

